import { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { X, Loader2, CheckCircle2, PhoneCall } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useUI } from "../context/UIContext";
import { createLead } from "../lib/api";

const TITLES = {
  beratung: "Kostenlose Beratung anfragen",
  rueckruf: "Rückruf vereinbaren",
  termin: "Termin anfragen",
  kontakt: "Nachricht senden",
};

export const LeadDialog = () => {
  const { leadOpen, setLeadOpen, leadConfig } = useUI();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const close = (v) => {
    setLeadOpen(v);
    if (!v) setTimeout(() => { setDone(false); setForm({ name: "", email: "", phone: "", message: "" }); }, 300);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error("Bitte Name und E-Mail angeben.");
    setLoading(true);
    try {
      await createLead({ ...form, type: leadConfig.type, topic: leadConfig.topic, source: "dialog" });
      setDone(true);
      toast.success("Ihre Anfrage ist eingegangen!");
    } catch {
      toast.error("Etwas ist schiefgelaufen. Bitte erneut versuchen.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={leadOpen} onOpenChange={close}>
      <DialogContent className="max-w-lg p-0 overflow-hidden border-0 rounded-3xl gap-0" data-testid="lead-dialog">
        <DialogTitle className="sr-only">{TITLES[leadConfig.type] || TITLES.beratung}</DialogTitle>
        <DialogDescription className="sr-only">Kostenlose und unverbindliche Anfrage – wir melden uns innerhalb von 24 Stunden.</DialogDescription>
        <div className="bg-blue-700 px-8 pt-8 pb-6 text-white relative">
          <button
            onClick={() => close(false)}
            className="absolute right-5 top-5 h-9 w-9 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            data-testid="lead-close-btn"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-blue-100 mb-3">
            <PhoneCall className="h-3.5 w-3.5" /> Unverbindlich & kostenlos
          </div>
          <h3 className="text-2xl font-heading font-semibold">{TITLES[leadConfig.type] || TITLES.beratung}</h3>
        </div>
        <div className="px-8 py-7 bg-white">
          {done ? (
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="text-center py-6" data-testid="lead-success">
              <CheckCircle2 className="h-14 w-14 text-blue-600 mx-auto mb-4" />
              <h4 className="font-heading text-xl text-navy-900 mb-2">Vielen Dank, {form.name.split(" ")[0]}!</h4>
              <p className="text-sm text-slate-500">Wir melden uns persönlich innerhalb von 24 Stunden bei Ihnen.</p>
              <button onClick={() => close(false)} className="mt-6 rounded-full bg-blue-700 text-white px-8 py-3 font-medium">Schließen</button>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="lead-form">
              <input required placeholder="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-transparent border-b-2 border-slate-200 focus:border-blue-500 outline-none py-2.5 text-sm transition-colors" data-testid="lead-name-input" />
              <input required type="email" placeholder="E-Mail *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-transparent border-b-2 border-slate-200 focus:border-blue-500 outline-none py-2.5 text-sm transition-colors" data-testid="lead-email-input" />
              <input placeholder="Telefon / WhatsApp" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full bg-transparent border-b-2 border-slate-200 focus:border-blue-500 outline-none py-2.5 text-sm transition-colors" data-testid="lead-phone-input" />
              <textarea placeholder="Ihre Nachricht (optional)" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full bg-transparent border-b-2 border-slate-200 focus:border-blue-500 outline-none py-2.5 text-sm transition-colors resize-none" data-testid="lead-message-input" />
              <button type="submit" disabled={loading} className="w-full rounded-full bg-blue-700 hover:bg-blue-600 disabled:opacity-60 text-white py-4 font-medium transition-colors flex items-center justify-center gap-2" data-testid="lead-submit-btn">
                {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Wird gesendet…</> : "Jetzt anfragen"}
              </button>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
